/**
 * Staging Authentication Utility
 *
 * Provides basic authentication for staging environment access.
 * Uses API validation for enhanced security.
 *
 * @author Dilan Useche <dilan8810@gmail.com>
 */

import authService from '@/api/modules/auth.service';

export function checkStagingAuth() {
  return new Promise((resolve) => {
    const isAuthenticated = localStorage.getItem('staging-auth') === 'true';

    if (isAuthenticated) {
      resolve(true);
      return;
    }

    showAuthModal(resolve);
  });
}

function showAuthModal(resolve) {
  // Remove any existing modal
  const existingModal = document.getElementById('staging-auth-modal');
  if (existingModal) {
    existingModal.remove();
  }

  // Create modal HTML
  const modalHTML = `
    <div id="staging-auth-modal" style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    ">
      <div style="
        background: white;
        padding: 2rem;
        border-radius: 8px;
        width: 90%;
        max-width: 400px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.3);
      ">
        <div style="text-align: center; margin-bottom: 1.5rem;">
          <div style="
            width: 60px;
            height: 60px;
            margin: 0 auto 1rem;
            background: #5D26AF;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <circle cx="12" cy="16" r="1"></circle>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
          <h2 style="margin: 0 0 0.5rem 0; color: #333; font-size: 1.5rem;">
            Staging Access
          </h2>
          <p style="margin: 0; color: #666; font-size: 0.9rem;">
            Enter credentials to access the staging environment
          </p>
        </div>
        <form id="staging-auth-form">
          <div style="margin-bottom: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 500; font-size: 0.9rem;">
              Username:
            </label>
            <input
              type="text"
              id="staging-username"
              required
              autocomplete="username"
              style="
                width: 100%;
                padding: 0.75rem;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 1rem;
                box-sizing: border-box;
                transition: border-color 0.2s;
              "
            />
          </div>
          <div style="margin-bottom: 1.5rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 500; font-size: 0.9rem;">
              Password:
            </label>
            <input
              type="password"
              id="staging-password"
              required
              autocomplete="current-password"
              style="
                width: 100%;
                padding: 0.75rem;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 1rem;
                box-sizing: border-box;
                transition: border-color 0.2s;
              "
            />
          </div>
          <div id="auth-error" style="
            color: #dc3545;
            margin-bottom: 1rem;
            text-align: center;
            font-size: 0.9rem;
            display: none;
          ">
            Invalid credentials. Please try again.
          </div>
          <button
            type="submit"
            id="staging-submit-btn"
            style="
              width: 100%;
              padding: 0.75rem;
              background: #5D26AF;
              color: white;
              border: none;
              border-radius: 4px;
              font-size: 1rem;
              cursor: pointer;
              font-weight: 500;
              transition: background-color 0.2s;
            "
            onmouseover="this.style.backgroundColor='#4a1f8f'"
            onmouseout="this.style.backgroundColor='#5D26AF'"
          >
            Access Staging
          </button>
        </form>
        <div style="
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #eee;
          text-align: center;
        ">
          <p style="
            margin: 0;
            color: #999;
            font-size: 0.8rem;
          ">
            GrapePerks Dashboard - Staging Environment
          </p>
        </div>
      </div>
    </div>
  `;

  // Add modal to DOM
  document.body.insertAdjacentHTML('beforeend', modalHTML);

  // Handle form submission
  const form = document.getElementById('staging-auth-form');
  const errorDiv = document.getElementById('auth-error');
  const usernameInput = document.getElementById('staging-username');
  const passwordInput = document.getElementById('staging-password');

  // Add focus styles
  [usernameInput, passwordInput].forEach(input => {
    input.addEventListener('focus', () => {
      input.style.borderColor = '#5D26AF';
      input.style.outline = 'none';
      input.style.boxShadow = '0 0 0 2px rgba(93, 38, 175, 0.2)';
    });

    input.addEventListener('blur', () => {
      input.style.borderColor = '#ddd';
      input.style.boxShadow = 'none';
    });
  });

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();

    const username = usernameInput?.value;
    const password = passwordInput?.value;
    const submitBtn = document.getElementById('staging-submit-btn');

    // Show loading state
    const originalText = submitBtn.textContent;
    submitBtn.textContent = 'Validating...';
    submitBtn.disabled = true;
    submitBtn.style.opacity = '0.7';
    errorDiv.style.display = 'none';

    try {
      // Validate credentials via API
      await authService.stagingAuth({ username, password });

      // Set authentication flag
      localStorage.setItem('staging-auth', 'true');

      // Remove modal with animation
      const modal = document.getElementById('staging-auth-modal');
      if (modal) {
        modal.style.opacity = '0';
        modal.style.transform = 'scale(0.9)';
        modal.style.transition = 'all 0.2s ease-out';

        setTimeout(() => {
          modal.remove();
          resolve(true);
        }, 200);
      }
    } catch (error) {
      // Reset button state
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
      submitBtn.style.opacity = '1';

      // Show error
      if (errorDiv) {
        errorDiv.style.display = 'block';
        // Show more specific error message if available
        if (error.response?.data?.message) {
          errorDiv.textContent = error.response.data.message;
        } else {
          errorDiv.textContent = 'Invalid credentials. Please try again.';
        }
      }

      // Clear password field
      if (passwordInput) {
        passwordInput.value = '';
      }

      // Shake animation for error
      const modalContent = document.querySelector('#staging-auth-modal > div');
      if (modalContent) {
        modalContent.style.animation = 'shake 0.5s ease-in-out';
        setTimeout(() => {
          modalContent.style.animation = '';
        }, 500);
      }
    }
  });

  // Focus username field
  setTimeout(() => {
    usernameInput?.focus();
  }, 100);

  // Add shake animation styles
  const style = document.createElement('style');
  style.textContent = `
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
      20%, 40%, 60%, 80% { transform: translateX(5px); }
    }
  `;
  document.head.appendChild(style);
}
